import React, { useEffect, useRef, useState, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  Rectangle,
  Polygon,
  Autocomplete,
} from '@react-google-maps/api';
import Loader from '../../Shared/Loader/Loader';
import api from '../../../utils/apiClient';
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import APIConstant from '../../../utils/pathConstants';
import { useCatchResponse } from '../../../utils/useCatchResponse';
import { isInsideFence, isSelfIntersecting } from '../../../utils/geofenceGeometry';

const containerStyle = { width: '100%', height: '360px' };
const defaultCenter = { lat: 23.219537206, lng: 70.319537206 };
const MAP_LIBRARIES = ['places'];

const TYPE_OPTIONS = [
  { label: 'Circular (fixed 200 m)', value: 'CircularFixed200' },
  { label: 'Custom Circular', value: 'CircularCustom' },
  { label: 'Custom Rectangle', value: 'RectangleCustom' },
  { label: 'Custom Polygon', value: 'PolygonCustom' },
];

const USER_TYPE_OPTIONS = [
  { label: 'Staff', value: 'Staff' },
  { label: 'Student', value: 'Student' },
];

const STAFF_OPTION_LABEL = 'Name';
const STAFF_OPTION_VALUE = '_id';

const AddEditGeofenceConfiguration = (props) => {
  const { catchResponse } = useCatchResponse();
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: APIConstant.path.GoogleMapApiKey,
    libraries: MAP_LIBRARIES,
  });
  const [securityUserID, setSecurityUserID] = useState('');
  const [organizationId, setOrganizationId] = useState('');

  const [geofenceName, setGeofenceName] = useState('');
  const [geofenceNameErr, setGeofenceNameErr] = useState('');
  const [parentGeofenceId, setParentGeofenceId] = useState('');
  const [attendanceTypeId, setAttendanceTypeId] = useState('');
  const [attendanceTypeErr, setAttendanceTypeErr] = useState('');
  const [attendanceTypeOptions, setAttendanceTypeOptions] = useState([]);
  const [userType, setUserType] = useState('');
  const [userTypeErr, setUserTypeErr] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [geofenceType, setGeofenceType] = useState('CircularFixed200');

  const [centerLat, setCenterLat] = useState(defaultCenter.lat);
  const [centerLng, setCenterLng] = useState(defaultCenter.lng);
  const [radius, setRadius] = useState(200);

  const [neLat, setNeLat] = useState(null);
  const [neLng, setNeLng] = useState(null);
  const [swLat, setSwLat] = useState(null);
  const [swLng, setSwLng] = useState(null);

  const [polyPoints, setPolyPoints] = useState([]);
  const [manualLat, setManualLat] = useState(null);
  const [manualLng, setManualLng] = useState(null);

  const polyPointsRef = useRef(polyPoints);
  polyPointsRef.current = polyPoints;
  const selectedParentRef = useRef(null);
  const pointBeforeEditRef = useRef(null);

  const [genericErr, setGenericErr] = useState('');
  const [mapRef, setMapRef] = useState(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    const loginData = CommonConfig.loginData();
    setSecurityUserID(loginData?._id);
    setOrganizationId(loginData?.OrganizationId || '');

    if (props.ID && props.masterData) {
      const m = props.masterData;
      setGeofenceName(m.GeofenceName || '');
      setParentGeofenceId(m.ParentGeofenceId || '');
      setAttendanceTypeId(m.AttendanceTypeId || '');
      setUserType(m.UserType || (Array.isArray(m.UserTypes) && m.UserTypes[0]) || '');
      setSelectedStaffIds(Array.isArray(m.StaffIds) ? m.StaffIds : []);
      setGeofenceType(m.GeofenceType || 'CircularFixed200');
      const c = m.Center || {};
      if (c.Lat != null) setCenterLat(Number(c.Lat));
      if (c.Lng != null) setCenterLng(Number(c.Lng));
      if (m.GeofenceType === 'CircularCustom' && m.RadiusMeters) {
        setRadius(Number(m.RadiusMeters));
      } else if (m.GeofenceType === 'CircularFixed200') {
        setRadius(200);
      }
      if (m.RectangleBounds) {
        setNeLat(m.RectangleBounds.NE?.Lat ?? null);
        setNeLng(m.RectangleBounds.NE?.Lng ?? null);
        setSwLat(m.RectangleBounds.SW?.Lat ?? null);
        setSwLng(m.RectangleBounds.SW?.Lng ?? null);
      }
      if (Array.isArray(m.PolygonPoints)) {
        setPolyPoints(m.PolygonPoints.map((p) => ({ lat: Number(p.Lat), lng: Number(p.Lng) })));
      }
    } else {
      handleCurrentLocation();
    }
    fetchStaffList(loginData);
    fetchAttendanceTypeList(loginData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAttendanceTypeList = async (loginData) => {
    try {
      const res = await api.post(APIConstant.path.getAttendanceTypeConfigurationList, { CreatedBy: loginData?._id });
      if (res.success) {
        const active = (res.data || []).filter((t) => t.Status === 'Active');
        setAttendanceTypeOptions(active);
      }
    } catch (err) {
      catchResponse(err);
    }
  };

  const fetchStaffList = async (loginData) => {
    try {
      const body = { SchoolId: loginData?._id };
      const res = await api.post(APIConstant.path.getStaffPickList, body);
      if (res.success) setStaffOptions(res.data || []);
    } catch (err) {
      catchResponse(err);
    }
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCenterLat(lat);
        setCenterLng(lng);
        mapRef?.panTo({ lat, lng });
      },
      () => { }
    );
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setCenterLat(lat);
      setCenterLng(lng);
      mapRef?.panTo({ lat, lng });
    }
  };

  const seedRectangle = useCallback(() => {
    if (neLat != null && neLng != null && swLat != null && swLng != null) return;
    const offset = 0.002;
    setNeLat(centerLat + offset);
    setNeLng(centerLng + offset);
    setSwLat(centerLat - offset);
    setSwLng(centerLng - offset);
  }, [centerLat, centerLng, neLat, neLng, swLat, swLng]);

  useEffect(() => {
    if (geofenceType === 'RectangleCustom') seedRectangle();
    if (geofenceType === 'CircularFixed200') setRadius(200);
  }, [geofenceType, seedRectangle]);

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    if (!guardPointInsideParent(lat, lng)) return;
    if (geofenceType === 'CircularFixed200' || geofenceType === 'CircularCustom') {
      setCenterLat(lat);
      setCenterLng(lng);
    } else if (geofenceType === 'PolygonCustom') {
      setPolyPoints((prev) => [...prev, { lat, lng }]);
    }
  };

  const onCircleCenterChanged = (circleRef) => {
    if (!circleRef) return;
    const c = circleRef.getCenter();
    if (c) {
      const lat = c.lat(), lng = c.lng();
      if (!guardPointInsideParent(lat, lng)) {
        circleRef.setCenter({ lat: centerLat, lng: centerLng });
        return;
      }
      setCenterLat(lat);
      setCenterLng(lng);
    }
  };

  const onCircleRadiusChanged = (circleRef) => {
    if (!circleRef || geofenceType !== 'CircularCustom') return;
    setRadius(Math.round(circleRef.getRadius()));
  };

  const onRectangleBoundsChanged = (rectRef) => {
    if (!rectRef) return;
    const b = rectRef.getBounds();
    if (!b) return;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    const neLatNew = ne.lat(), neLngNew = ne.lng(), swLatNew = sw.lat(), swLngNew = sw.lng();
    if (selectedParent) {
      const midLat = (neLatNew + swLatNew) / 2, midLng = (neLngNew + swLngNew) / 2;
      const cornersInside = [
        [neLatNew, neLngNew],
        [neLatNew, swLngNew],
        [swLatNew, neLngNew],
        [swLatNew, swLngNew],
        [midLat, midLng],
      ].every(([la, ln]) => isInsideFence(la, ln, selectedParent));
      if (!cornersInside) {
        Toast.error({ message: `Rectangle exceeds "${selectedParent.GeofenceName}". Sub-geofence must stay inside its parent.` });
        if (rectangleBoundsProp) rectRef.setBounds(rectangleBoundsProp);
        return;
      }
    }
    setNeLat(neLatNew);
    setNeLng(neLngNew);
    setSwLat(swLatNew);
    setSwLng(swLngNew);
  };

  const onPolygonEdited = useCallback((polyRef) => {
    if (!polyRef) return;
    const path = polyRef.getPath();
    const pts = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      pts.push({ lat: p.lat(), lng: p.lng() });
    }
    const parent = selectedParentRef.current;
    if (parent && !pts.every((p) => isInsideFence(p.lat, p.lng, parent))) {
      Toast.error({ message: `Polygon has points outside "${parent.GeofenceName}". Sub-geofence must stay inside its parent.` });
      // Revert through state, not polyRef.setPath: a new array identity makes the library
      // rebuild the path, which rebinds the listeners bound to the discarded one.
      setPolyPoints([...polyPointsRef.current]);
      return;
    }
    setPolyPoints(pts);
  }, []);

  const [polyInstance, setPolyInstance] = useState(null);

  const attachPolyListeners = useCallback((poly) => {
    activePolyRef.current = poly;
    setPolyInstance(poly);
  }, []);

  const detachPolyListeners = useCallback(() => {
    activePolyRef.current = null;
    setPolyInstance(null);
  }, []);

  // setPath() replaces the path object, so listeners must be rebound whenever polyPoints changes.
  useEffect(() => {
    if (!polyInstance) return undefined;
    const path = polyInstance.getPath();
    const handler = () => onPolygonEdited(polyInstance);
    const listeners = [
      path.addListener('set_at', handler),
      path.addListener('insert_at', handler),
      path.addListener('remove_at', handler),
    ];
    return () => listeners.forEach((l) => l.remove());
  }, [polyInstance, polyPoints, onPolygonEdited]);

  const removePolyPoint = (idx) => {
    setPolyPoints((prev) => prev.filter((_, i) => i !== idx));
  };

  const undoPolyPoint = () => {
    setPolyPoints((prev) => prev.slice(0, -1));
  };

  const clearPolygon = () => setPolyPoints([]);

  const updatePolyPoint = (idx, key, value) => {
    if (value == null || !Number.isFinite(Number(value))) return;
    const num = Number(value);
    const limit = key === 'lat' ? 90 : 180;
    if (num < -limit || num > limit) return;
    setPolyPoints((prev) => prev.map((p, i) => (i === idx ? { ...p, [key]: num } : p)));
  };

  const onPolyPointFocus = (idx) => {
    pointBeforeEditRef.current = { idx, point: polyPoints[idx] };
  };

  const onPolyPointBlur = (idx) => {
    const p = polyPointsRef.current[idx];
    const previous = pointBeforeEditRef.current;
    if (!p || !previous || previous.idx !== idx) return;
    if (guardPointInsideParent(p.lat, p.lng)) return;
    setPolyPoints((prev) => prev.map((q, i) => (i === idx ? previous.point : q)));
  };

  const addManualPoint = () => {
    if (manualLat == null || manualLng == null || !Number.isFinite(Number(manualLat)) || !Number.isFinite(Number(manualLng))) {
      Toast.error({ message: 'Enter both latitude and longitude.' });
      return;
    }
    const lat = Number(manualLat);
    const lng = Number(manualLng);
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      Toast.error({ message: 'Latitude must be between -90 and 90, longitude between -180 and 180.' });
      return;
    }
    if (!guardPointInsideParent(lat, lng)) return;
    setPolyPoints((prev) => [...prev, { lat, lng }]);
    setManualLat(null);
    setManualLng(null);
    mapRef?.panTo({ lat, lng });
  };

  const validate = () => {
    let ok = true;
    setGenericErr('');
    if (CommonConfig.isEmpty(geofenceName)) {
      ok = false;
      setGeofenceNameErr('Geofence Name is required');
    } else {
      setGeofenceNameErr('');
    }
    if (!parentGeofenceId && CommonConfig.isEmpty(userType)) {
      ok = false;
      setUserTypeErr('User Type is required');
    } else {
      setUserTypeErr('');
    }
    if (!parentGeofenceId && CommonConfig.isEmpty(attendanceTypeId)) {
      ok = false;
      setAttendanceTypeErr('Attendance Type is required');
    } else {
      setAttendanceTypeErr('');
    }
    if (geofenceType === 'CircularCustom' && (!radius || radius <= 0)) {
      ok = false;
      setGenericErr('Radius must be greater than 0');
    }
    if (geofenceType === 'RectangleCustom') {
      if (neLat == null || neLng == null || swLat == null || swLng == null) {
        ok = false;
        setGenericErr('Rectangle bounds are required');
      }
    }
    if (geofenceType === 'PolygonCustom') {
      if (polyPoints.length < 3) {
        ok = false;
        setGenericErr('Polygon requires at least 3 points');
      } else if (isSelfIntersecting(polyPoints.map((p) => ({ Lat: p.lat, Lng: p.lng })))) {
        ok = false;
        setGenericErr('Polygon edges cross each other. Reorder or move the points so the outline does not overlap itself.');
      }
    }
    return ok;
  };

  const buildPayload = () => {
    const payload = {
      id: props.ID || undefined,
      GeofenceName: geofenceName.trim().replace(/\s+/g, ' '),
      GeofenceType: geofenceType,
      ParentGeofenceId: parentGeofenceId || null,
      AttendanceTypeId: attendanceTypeId || null,
      UserType: userType,
      StaffIds: userType === 'Staff' ? selectedStaffIds : [],
      OrganizationId: organizationId,
      CreatedBy: securityUserID,
      UpdatedBy: securityUserID,
    };
    if (geofenceType === 'CircularFixed200') {
      payload.Center = { Lat: centerLat, Lng: centerLng };
      payload.RadiusMeters = 200;
    } else if (geofenceType === 'CircularCustom') {
      payload.Center = { Lat: centerLat, Lng: centerLng };
      payload.RadiusMeters = Number(radius);
    } else if (geofenceType === 'RectangleCustom') {
      payload.RectangleBounds = {
        NE: { Lat: Number(neLat), Lng: Number(neLng) },
        SW: { Lat: Number(swLat), Lng: Number(swLng) },
      };
    } else if (geofenceType === 'PolygonCustom') {
      payload.PolygonPoints = polyPoints.map((p) => ({ Lat: p.lat, Lng: p.lng }));
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      Loader.show();
      await api
        .post(APIConstant.path.AddUpdateGeofenceConfiguration, buildPayload())
        .then((res) => {
          if (res.success) {
            Toast.success({ message: res.message });
            props.onCancel();
          } else {
            Toast.error({ message: res.message });
          }
          Loader.hide();
        })
        .catch((err) => {
          Loader.hide();
          catchResponse(err);
        });
    } catch (err) {
      Loader.hide();
      catchResponse(err);
    }
  };

  const activeCircleRef = useRef(null);
  const activeRectRef = useRef(null);
  const activePolyRef = useRef(null);

  const selectedParent = (props.parentOptions || []).find((p) => String(p._id) === String(parentGeofenceId));
  selectedParentRef.current = selectedParent;

  useEffect(() => {
    if (!selectedParent) return;
    if (isInsideFence(Number(centerLat), Number(centerLng), selectedParent)) return;
    const p = selectedParent;
    if ((p.GeofenceType === 'CircularFixed200' || p.GeofenceType === 'CircularCustom') && p.Center) {
      setCenterLat(Number(p.Center.Lat));
      setCenterLng(Number(p.Center.Lng));
      mapRef?.panTo({ lat: Number(p.Center.Lat), lng: Number(p.Center.Lng) });
    } else if (p.GeofenceType === 'RectangleCustom' && p.RectangleBounds?.NE && p.RectangleBounds?.SW) {
      const mLat = (Number(p.RectangleBounds.NE.Lat) + Number(p.RectangleBounds.SW.Lat)) / 2;
      const mLng = (Number(p.RectangleBounds.NE.Lng) + Number(p.RectangleBounds.SW.Lng)) / 2;
      setCenterLat(mLat);
      setCenterLng(mLng);
      mapRef?.panTo({ lat: mLat, lng: mLng });
    } else if (p.GeofenceType === 'PolygonCustom' && Array.isArray(p.PolygonPoints) && p.PolygonPoints.length) {
      const s = p.PolygonPoints.reduce((a, pt) => ({ Lat: a.Lat + Number(pt.Lat), Lng: a.Lng + Number(pt.Lng) }), { Lat: 0, Lng: 0 });
      const cLat = s.Lat / p.PolygonPoints.length, cLng = s.Lng / p.PolygonPoints.length;
      setCenterLat(cLat);
      setCenterLng(cLng);
      mapRef?.panTo({ lat: cLat, lng: cLng });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentGeofenceId]);

  const guardPointInsideParent = (lat, lng) => {
    if (!selectedParent) return true;
    if (isInsideFence(lat, lng, selectedParent)) return true;
    Toast.error({ message: `Point is outside "${selectedParent.GeofenceName}". Sub-geofence must stay inside its parent.` });
    return false;
  };

  const parentOverlayOptions = {
    fillOpacity: 0,
    strokeColor: '#c62828',
    strokeWeight: 2,
    strokeOpacity: 0.9,
    clickable: false,
    zIndex: 1,
  };
  const parentRectangleBounds =
    selectedParent?.GeofenceType === 'RectangleCustom' && selectedParent?.RectangleBounds?.NE && selectedParent?.RectangleBounds?.SW
      ? {
        north: Number(selectedParent.RectangleBounds.NE.Lat),
        east: Number(selectedParent.RectangleBounds.NE.Lng),
        south: Number(selectedParent.RectangleBounds.SW.Lat),
        west: Number(selectedParent.RectangleBounds.SW.Lng),
      }
      : null;
  const parentPolygonPath =
    selectedParent?.GeofenceType === 'PolygonCustom'
      ? (selectedParent.PolygonPoints || []).map((p) => ({ lat: Number(p.Lat), lng: Number(p.Lng) }))
      : [];
  const parentCircleRadius =
    selectedParent?.GeofenceType === 'CircularFixed200'
      ? 200
      : selectedParent?.GeofenceType === 'CircularCustom'
        ? Number(selectedParent.RadiusMeters) || 0
        : 0;

  const rectangleBoundsProp =
    neLat != null && neLng != null && swLat != null && swLng != null
      ? { north: Number(neLat), east: Number(neLng), south: Number(swLat), west: Number(swLng) }
      : null;

  const mapCenter = { lat: Number(centerLat) || defaultCenter.lat, lng: Number(centerLng) || defaultCenter.lng };

  return (
    <div className="geofence-form">
      <div className="p-grid">
        <div className="p-col-12">
          <div className="gf-section-title"><i className="pi pi-sliders-h" />Geofence Details</div>
        </div>
        <div className="p-col-12 p-md-6 p-pt-2">
          <label>Geofence Name<span style={{ color: 'red' }}>*</span></label>
          <div className="custom-inputs">
            <InputText
              className="p-mt-2 p-pl-2 p-w-100"
              maxLength={200}
              placeholder="Geofence Name"
              value={geofenceName}
              onChange={(e) => setGeofenceName(e.target.value)}
            />
            <span className="error-msg" style={{ color: 'red' }}>{geofenceNameErr}</span>
          </div>
        </div>

        <div className="p-col-12 p-md-6 p-pt-2">
          <label>Parent Geofence <small style={{ color: '#888' }}>(leave empty to create a top-level geofence)</small></label>
          <div className="custom-inputs">
            <Dropdown
              className="p-mt-2 p-w-100"
              value={parentGeofenceId}
              options={(props.parentOptions || []).filter((p) => !props.ID || String(p._id) !== String(props.ID))}
              optionLabel="GeofenceName"
              optionValue="_id"
              onChange={(e) => setParentGeofenceId(e.value || '')}
              placeholder="Select parent (optional)"
              showClear
            />
          </div>
        </div>

        {!parentGeofenceId && (
          <div className="p-col-12 p-md-6 p-pt-2">
            <label>Attendance Type<span style={{ color: 'red' }}>*</span></label>
            <div className="custom-inputs">
              <Dropdown
                className="p-mt-2 p-w-100"
                value={attendanceTypeId}
                options={attendanceTypeOptions}
                optionLabel="AttendanceName"
                optionValue="_id"
                onChange={(e) => setAttendanceTypeId(e.value || '')}
                placeholder="Select attendance type"
                showClear
              />
              <span className="error-msg" style={{ color: 'red' }}>{attendanceTypeErr}</span>
            </div>
          </div>
        )}

        {parentGeofenceId && (
          <div className="p-col-12 p-md-6 p-pt-2">
            <label>Attendance Type <small style={{ color: '#888' }}>(inherited from parent)</small></label>
            <div className="custom-inputs">
              <InputText
                className="p-mt-2 p-pl-2 p-w-100"
                value={
                  (attendanceTypeOptions.find((t) => String(t._id) === String(selectedParent?.AttendanceTypeId))?.AttendanceName) || '—'
                }
                disabled
              />
            </div>
          </div>
        )}

        {!parentGeofenceId && (
          <div className="p-col-12 p-md-6 p-pt-2">
            <label>User Type<span style={{ color: 'red' }}>*</span></label>
            <div className="custom-inputs">
              <Dropdown
                className="p-mt-2 p-w-100"
                value={userType}
                options={USER_TYPE_OPTIONS}
                onChange={(e) => {
                  setUserType(e.value);
                  if (e.value !== 'Staff') setSelectedStaffIds([]);
                }}
                placeholder="Select user type"
              />
              <span className="error-msg" style={{ color: 'red' }}>{userTypeErr}</span>
            </div>
          </div>
        )}

        {!parentGeofenceId && userType === 'Staff' && (
          <div className="p-col-12 p-md-6 p-pt-2">
            <label>Staff Members <small style={{ color: '#888' }}>(leave empty to apply to all staff)</small></label>
            <div className="custom-inputs">
              <MultiSelect
                className="p-mt-2 p-w-100"
                value={selectedStaffIds}
                options={staffOptions}
                optionLabel={STAFF_OPTION_LABEL}
                optionValue={STAFF_OPTION_VALUE}
                onChange={(e) => setSelectedStaffIds(e.value)}
                placeholder="Select staff (optional)"
                display="chip"
                filter
              />
            </div>
          </div>
        )}

        <div className="p-col-12 p-md-6 p-pt-2">
          <label>Geofence Type<span style={{ color: 'red' }}>*</span></label>
          <div className="custom-inputs">
            <Dropdown
              className="p-mt-2 p-w-100"
              value={geofenceType}
              options={TYPE_OPTIONS}
              onChange={(e) => setGeofenceType(e.value)}
              placeholder="Select type"
            />
          </div>
        </div>

        <div className="p-col-12">
          <div className="gf-section-title"><i className="pi pi-compass" />Area Definition</div>
        </div>

        {(geofenceType === 'CircularFixed200' || geofenceType === 'CircularCustom') && (
          <>
            <div className="p-col-12 p-md-3 p-pt-2">
              <label>Latitude</label>
              <div className="custom-inputs">
                <InputNumber
                  className="p-mt-2 p-w-100"
                  value={centerLat}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && !guardPointInsideParent(v, centerLng)) {
                      setCenterLat(centerLat);
                    }
                  }}
                  onValueChange={(e) => setCenterLat(e.value ?? 0)}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={8}
                  useGrouping={false}
                />
              </div>
            </div>
            <div className="p-col-12 p-md-3 p-pt-2">
              <label>Longitude</label>
              <div className="custom-inputs">
                <InputNumber
                  className="p-mt-2 p-w-100"
                  value={centerLng}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && !guardPointInsideParent(centerLat, v)) {
                      setCenterLng(centerLng);
                    }
                  }}
                  onValueChange={(e) => setCenterLng(e.value ?? 0)}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={8}
                  useGrouping={false}
                />
              </div>
            </div>
            <div className="p-col-12 p-md-6 p-pt-2">
              <label>Radius (meters)</label>
              <div className="custom-inputs">
                <InputNumber
                  className="p-mt-2 p-w-100"
                  value={radius}
                  onValueChange={(e) => setRadius(e.value ?? 0)}
                  disabled={geofenceType === 'CircularFixed200'}
                  min={1}
                  useGrouping={false}
                />
              </div>
            </div>
          </>
        )}

        {geofenceType === 'RectangleCustom' && (
          <>
            <div className="p-col-12 p-md-3 p-pt-2">
              <label>NE Lat</label>
              <InputNumber
                className="p-mt-2 p-w-100"
                value={neLat}
                onValueChange={(e) => setNeLat(e.value)}
                onBlur={() => {
                  if (selectedParent && neLat != null && neLng != null && !isInsideFence(Number(neLat), Number(neLng), selectedParent)) {
                    Toast.error({ message: `NE corner is outside "${selectedParent.GeofenceName}".` });
                  }
                }}
                mode="decimal" maxFractionDigits={8} useGrouping={false}
              />
            </div>
            <div className="p-col-12 p-md-3 p-pt-2">
              <label>NE Lng</label>
              <InputNumber
                className="p-mt-2 p-w-100"
                value={neLng}
                onValueChange={(e) => setNeLng(e.value)}
                onBlur={() => {
                  if (selectedParent && neLat != null && neLng != null && !isInsideFence(Number(neLat), Number(neLng), selectedParent)) {
                    Toast.error({ message: `NE corner is outside "${selectedParent.GeofenceName}".` });
                  }
                }}
                mode="decimal" maxFractionDigits={8} useGrouping={false}
              />
            </div>
            <div className="p-col-12 p-md-3 p-pt-2">
              <label>SW Lat</label>
              <InputNumber
                className="p-mt-2 p-w-100"
                value={swLat}
                onValueChange={(e) => setSwLat(e.value)}
                onBlur={() => {
                  if (selectedParent && swLat != null && swLng != null && !isInsideFence(Number(swLat), Number(swLng), selectedParent)) {
                    Toast.error({ message: `SW corner is outside "${selectedParent.GeofenceName}".` });
                  }
                }}
                mode="decimal" maxFractionDigits={8} useGrouping={false}
              />
            </div>
            <div className="p-col-12 p-md-3 p-pt-2">
              <label>SW Lng</label>
              <InputNumber
                className="p-mt-2 p-w-100"
                value={swLng}
                onValueChange={(e) => setSwLng(e.value)}
                onBlur={() => {
                  if (selectedParent && swLat != null && swLng != null && !isInsideFence(Number(swLat), Number(swLng), selectedParent)) {
                    Toast.error({ message: `SW corner is outside "${selectedParent.GeofenceName}".` });
                  }
                }}
                mode="decimal" maxFractionDigits={8} useGrouping={false}
              />
            </div>
          </>
        )}

        {geofenceType === 'PolygonCustom' && (
          <div className="p-col-12 p-pt-2">
            <div className="p-d-flex p-jc-between p-ai-center">
              <label>Polygon Points ({polyPoints.length} · min 3) — click the map, drag a corner, or type below</label>
              <div>
                <Button
                  label="Undo"
                  icon="pi pi-undo"
                  className="p-button-sm p-button-text"
                  onClick={undoPolyPoint}
                  disabled={polyPoints.length === 0}
                  type="button"
                />
                <Button
                  label="Clear"
                  className="p-button-sm p-button-text"
                  onClick={clearPolygon}
                  disabled={polyPoints.length === 0}
                  type="button"
                />
              </div>
            </div>
            <div className="gf-poly-list">
              {polyPoints.length === 0 && <span style={{ color: '#888' }}>No points yet. Click on the map or add coordinates manually.</span>}
              {polyPoints.map((p, i) => (
                <div key={i} className="p-d-flex p-ai-center" style={{ marginBottom: 4, gap: 6 }}>
                  <span style={{ width: 22, color: '#888' }}>{i + 1}.</span>
                  <InputNumber
                    value={p.lat}
                    onFocus={() => onPolyPointFocus(i)}
                    onBlur={() => onPolyPointBlur(i)}
                    onValueChange={(e) => updatePolyPoint(i, 'lat', e.value)}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={8}
                    useGrouping={false}
                    inputStyle={{ width: 130 }}
                  />
                  <InputNumber
                    value={p.lng}
                    onFocus={() => onPolyPointFocus(i)}
                    onBlur={() => onPolyPointBlur(i)}
                    onValueChange={(e) => updatePolyPoint(i, 'lng', e.value)}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={8}
                    useGrouping={false}
                    inputStyle={{ width: 130 }}
                  />
                  <Button
                    icon="pi pi-times"
                    className="p-button-sm p-button-text"
                    onClick={() => removePolyPoint(i)}
                    type="button"
                  />
                </div>
              ))}
            </div>
            <div className="p-d-flex p-ai-center p-mt-2" style={{ gap: 6 }}>
              <InputNumber
                value={manualLat}
                onValueChange={(e) => setManualLat(e.value)}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={8}
                useGrouping={false}
                placeholder="Latitude"
                inputStyle={{ width: 130 }}
              />
              <InputNumber
                value={manualLng}
                onValueChange={(e) => setManualLng(e.value)}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={8}
                useGrouping={false}
                placeholder="Longitude"
                inputStyle={{ width: 130 }}
              />
              <Button label="Add Point" icon="pi pi-plus" className="p-button-sm" onClick={addManualPoint} type="button" />
            </div>
          </div>
        )}

        <div className="p-col-12">
          <div className="gf-section-title"><i className="pi pi-map-marker" />Location</div>
        </div>

        <div className="p-col-12 p-pt-2">
          <div className="gf-map-card">
            {!isMapLoaded ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Loading map...</div>
            ) : (
              <>
                <div className="p-grid" style={{ position: 'relative' }}>
                  <Autocomplete
                    onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                    onPlaceChanged={handlePlaceSelect}
                    options={{ fields: ['geometry', 'name', 'formatted_address'] }}
                  >
                    <div className="p-pt-2 p-pl-2 custom-inputs" style={{ flex: 1 }}>
                      <InputText
                        className="p-w-100"
                        placeholder="Search location..."
                        style={{ width: '100%', padding: 10, marginBottom: 10, borderRadius: 5, border: '1px solid #ccc', zIndex: 10001 }}
                      />
                    </div>
                  </Autocomplete>
                  <Button
                    icon="pi pi-map-marker"
                    className="e-btn p-mr-0 p-mt-2 p-ml-2"
                    onClick={handleCurrentLocation}
                    type="button"
                  />
                </div>
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={mapCenter}
                  zoom={15}
                  onClick={handleMapClick}
                  onLoad={(map) => setMapRef(map)}
                >
                  {selectedParent && (selectedParent.GeofenceType === 'CircularFixed200' || selectedParent.GeofenceType === 'CircularCustom') && selectedParent.Center && (
                    <Circle
                      center={{ lat: Number(selectedParent.Center.Lat), lng: Number(selectedParent.Center.Lng) }}
                      radius={parentCircleRadius}
                      options={parentOverlayOptions}
                    />
                  )}
                  {selectedParent && selectedParent.GeofenceType === 'RectangleCustom' && parentRectangleBounds && (
                    <Rectangle bounds={parentRectangleBounds} options={parentOverlayOptions} />
                  )}
                  {selectedParent && selectedParent.GeofenceType === 'PolygonCustom' && parentPolygonPath.length > 0 && (
                    <Polygon path={parentPolygonPath} options={parentOverlayOptions} />
                  )}
                  {(geofenceType === 'CircularFixed200' || geofenceType === 'CircularCustom') && (
                    <>
                      <Marker
                        position={mapCenter}
                        draggable
                        onDragEnd={(e) => {
                          const lat = e.latLng.lat(), lng = e.latLng.lng();
                          if (!guardPointInsideParent(lat, lng)) return;
                          setCenterLat(lat);
                          setCenterLng(lng);
                        }}
                      />
                      <Circle
                        onLoad={(c) => (activeCircleRef.current = c)}
                        center={mapCenter}
                        radius={Number(radius) || 200}
                        editable={geofenceType === 'CircularCustom'}
                        draggable={geofenceType === 'CircularCustom'}
                        onCenterChanged={() => onCircleCenterChanged(activeCircleRef.current)}
                        onRadiusChanged={() => onCircleRadiusChanged(activeCircleRef.current)}
                        options={{ fillColor: '#1E90FF', fillOpacity: 0.2, strokeColor: '#1E90FF', strokeWeight: 2 }}
                      />
                    </>
                  )}

                  {geofenceType === 'RectangleCustom' && rectangleBoundsProp && (
                    <Rectangle
                      onLoad={(r) => (activeRectRef.current = r)}
                      bounds={rectangleBoundsProp}
                      editable
                      draggable
                      onBoundsChanged={() => onRectangleBoundsChanged(activeRectRef.current)}
                      options={{ fillColor: '#1E90FF', fillOpacity: 0.2, strokeColor: '#1E90FF', strokeWeight: 2 }}
                    />
                  )}

                  {geofenceType === 'PolygonCustom' && polyPoints.length > 0 && (
                    <Polygon
                      onLoad={attachPolyListeners}
                      onUnmount={detachPolyListeners}
                      path={polyPoints}
                      editable
                      draggable
                      onDragEnd={() => onPolygonEdited(activePolyRef.current)}
                      options={{ fillColor: '#1E90FF', fillOpacity: 0.2, strokeColor: '#1E90FF', strokeWeight: 2 }}
                    />
                  )}
                </GoogleMap>
              </>
            )}
          </div>
        </div>

        {genericErr && (
          <div className="p-col-12">
            <span className="error-msg" style={{ color: 'red' }}>{genericErr}</span>
          </div>
        )}
      </div>

      <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
        <Button className="btn-text-action-secondary" onClick={() => props.onCancel()} type="button">Cancel</Button>
        <Button className="btn-text-action-primary submit-focus" label="Save" onClick={handleSubmit} type="button" />
      </div>
    </div>
  );
};

export default AddEditGeofenceConfiguration;
